import { useMemo } from 'react'

import {
  DonutChart,
  StatCard,
  StatCardsSkeleton,
  StatLegend,
  StatText,
} from '@/components/stats/StatCard'
import type { ChartConfig } from '@/components/ui/chart'
import type { RecordSummary } from '@/types/api/record'
import type { RecordGroupSummary } from '@/types/api/record-group'
import { RecordSourceType } from '@/types/enums'

interface RecordStatsProps {
  summary: RecordSummary | undefined
  groupSummary: RecordGroupSummary | undefined
  isLoading?: boolean
}

const COLORS = {
  records: { primary: '#2563eb', secondary: '#bfdbfe' },
  sources: {
    [RecordSourceType.INTERNAL_USER]: '#10b981',
    [RecordSourceType.PUBLIC_FORM]: '#0ea5e9',
    [RecordSourceType.CSV_IMPORT]: '#f59e0b',
  },
  tags: { primary: '#7c3aed', secondary: '#ddd6fe' },
  groups: { primary: '#059669', secondary: '#a7f3d0' },
} as const

const SOURCE_LABELS: Record<RecordSourceType, string> = {
  [RecordSourceType.INTERNAL_USER]: 'Internal',
  [RecordSourceType.PUBLIC_FORM]: 'Public form',
  [RecordSourceType.CSV_IMPORT]: 'CSV import',
}

const recordsConfig = {
  recent: { label: 'Last 30 days', color: COLORS.records.primary },
  older: { label: 'Older', color: COLORS.records.secondary },
} satisfies ChartConfig
const sourceConfig = Object.fromEntries(
  Object.values(RecordSourceType).map((s) => [s, { label: SOURCE_LABELS[s], color: COLORS.sources[s] }])
) satisfies ChartConfig
const tagConfig = {
  tagged: { label: 'Tagged', color: COLORS.tags.primary },
  untagged: { label: 'Untagged', color: COLORS.tags.secondary },
} satisfies ChartConfig
const groupConfig = {
  direct: { label: 'Direct', color: COLORS.groups.primary },
  nested: { label: 'Nested', color: COLORS.groups.secondary },
} satisfies ChartConfig

/** Overview cards for one record group, in the same style as the Record Groups overview. */
export function RecordStats({ summary, groupSummary, isLoading = false }: RecordStatsProps) {
  const stats = useMemo(() => {
    const total = summary?.total ?? 0
    const recent = summary?.createdLast30Days ?? 0
    const tagged = summary?.tagged ?? 0
    const tagCoverage = total > 0 ? Math.round((tagged / total) * 100) : 0
    return { total, recent, tagged, tagCoverage }
  }, [summary])

  if (isLoading || !summary) {
    return <StatCardsSkeleton label="Loading record overview" className="xl:grid-cols-2" />
  }

  const sources = Object.values(RecordSourceType).map((source) => ({
    source,
    value: summary.bySource[source] ?? 0,
  }))
  const direct = groupSummary?.topLevel ?? summary.childGroups
  const nested = groupSummary?.nested ?? 0

  return (
    <section aria-label="Record overview" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-2">
      <StatCard>
        <StatText title="Records" value={stats.total} caption={`${stats.recent} added in the last 30 days`} />
        <DonutChart
          data={[
            { segment: 'recent', value: stats.recent, fill: COLORS.records.primary },
            { segment: 'older', value: stats.total - stats.recent, fill: COLORS.records.secondary },
          ]}
          config={recordsConfig}
          center={String(stats.total)}
          caption="Records"
        />
      </StatCard>
      <StatCard>
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-muted-foreground">By Source</h3>
          <StatLegend
            items={sources.map(({ source, value }) => ({ label: SOURCE_LABELS[source], value, color: COLORS.sources[source] }))}
          />
        </div>
        <DonutChart
          data={sources.map(({ source, value }) => ({ segment: source, value, fill: COLORS.sources[source] }))}
          config={sourceConfig}
          center={String(stats.total)}
          caption="Records"
        />
      </StatCard>
      <StatCard>
        <StatText
          title="Tag Coverage"
          value={`${stats.tagCoverage}%`}
          caption={`${stats.tagged} of ${stats.total} tagged`}
        />
        <DonutChart
          data={[
            { segment: 'tagged', value: stats.tagged, fill: COLORS.tags.primary },
            { segment: 'untagged', value: stats.total - stats.tagged, fill: COLORS.tags.secondary },
          ]}
          config={tagConfig}
          center={`${stats.tagCoverage}%`}
        />
      </StatCard>
      <StatCard>
        <StatText
          title="Child Groups"
          value={direct + nested}
          caption={`${direct} direct · ${nested} nested`}
        />
        <DonutChart
          data={[
            { segment: 'direct', value: direct, fill: COLORS.groups.primary },
            { segment: 'nested', value: nested, fill: COLORS.groups.secondary },
          ]}
          config={groupConfig}
          center={String(direct + nested)}
          caption="Groups"
        />
      </StatCard>
    </section>
  )
}
